import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, Stack, Tooltip } from '@mui/material';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import StatusIcon from '@sharedCR/common/StatusIcon';
import { InfoIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { LoanOptionVendorType } from 'constants/company';
import { CENTRAL_TIME_ZONE, COMMON_DATETIME_WITH_ZONE_FORMAT } from 'constants/date';
import { fixedTwoFractionDigitsCurrencyOptions } from 'constants/loanApplication';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { StatusEnum } from 'constants/status';
import { HORIZONTAL_ALIGNMENT, TABLE_CELL_ALIGNMENT } from 'constants/ui';
import { formatDate } from 'utils/date';
import { formatNumberToCurrencyString, formatNumberToFixed3Decimals } from 'utils/number';

import {
  selectIsLoanOptimalBlueOrSifterEngineType,
  selectRateSheetModificationDate,
} from 'store/loanApplications/selectors';
import { selectCustomAdjustmentsValues } from 'store/programs/selectors';

import { ColumnIds } from '../../../../LoanInfoModal.constant';
import { ExpiredPriceWarningMessage } from '../../ProgramsAndPricing.constants';
import { ProgramsAndPricingTestIds } from '../../ProgramsAndPricing.testIds';
import { ColumnDefShape } from '../../ProgramsAndPricing.types';

import CellContent from './components/CellContent';
import { CellContentType } from './components/CellContent/CellContent.constants';
import { creditOrCostCurrencyOptions } from './ProgramSummary.constants';
import {
  amountCellContentStyles,
  eligibilityTooltipStyles,
  getCellStyles,
  programCellContentStyles,
  programSummaryStyles,
  tooltipPopperProps,
  tooltipWrapperStyles,
  unselectedProgramLabelStyles,
} from './ProgramSummary.styles';
import { getEligibilityTooltip } from './ProgramSummary.utils';

const defaultObject = {};

const ProgramSummary = ({
  columnsDefs,
  programDetails,
  currentLoanProgramOption,
  isPendingInitProgram = false,
  isPendingNewProgram = false,
  isPendingProgramChange = false,
  isProgramExpired = false,
  isSelectedLoanProgramOption = false,
  pendingChanges = defaultObject,
}) => {
  const customAdjustments = useSelector(selectCustomAdjustmentsValues);
  const isLoanOptimalBlueOrSifterPPEType = useSelector(selectIsLoanOptimalBlueOrSifterEngineType);
  const rateSheetModificationDate = useSelector(selectRateSheetModificationDate);

  const isStubLoanOption = currentLoanProgramOption.isStubOption;
  const { eligibility, program, compensation } = programDetails ?? {};
  const isEligible = !!eligibility?.isAllowed;

  // Pricing freshness is the quoted rate sheet's date — program.lastModified is the program's own config
  // audit stamp, which a rate sheet upload never touches. Optimal Blue / LoanSifter quotes carry no rate
  // sheet, so those fall back to the product date.
  const lastUpdatedDate = rateSheetModificationDate ?? program?.lastModified;
  const lastUpdatedDateStr = lastUpdatedDate
    ? formatDate(lastUpdatedDate, COMMON_DATETIME_WITH_ZONE_FORMAT, {
        timeZone: CENTRAL_TIME_ZONE,
      })
    : '';

  const { searchId, internalName: programInternalName, vendorType } = program;
  // ProgramDetails.creditOrCost is a per-program field the pricing engines never populate (always 0); the dollar
  // credit/cost lives on the loan OPTION the row renders (par / discount-point / lender-credit). DEV-5681.
  const {
    rate,
    apr,
    paymentMonth,
    lockTerm,
    pmi,
    discountAmount: creditOrCost,
  } = currentLoanProgramOption;

  const lenderName =
    program.lender?.investorIdentity?.name ||
    program.lenderName ||
    currentLoanProgramOption.lenderName;

  const cellContentRenderer = useCallback(
    ({ columnId, getTooltip, iconButtonProps }) => {
      switch (columnId) {
        case ColumnIds.INVESTOR: {
          const investorSourceValue =
            vendorType === LoanOptionVendorType.OPTIMAL_BLUE ? lenderName : vendorType;

          return (
            <Stack sx={programCellContentStyles}>
              <CellContent sourceValue={investorSourceValue}>
                {_.toUpper(investorSourceValue)}
              </CellContent>
              {isPendingInitProgram && !isPendingNewProgram && (
                <CellContent sourceValue type={CellContentType.SECONDARY}>
                  <PendingChangeWrapper
                    isSmall
                    pendingChangesActionType={PendingChangesActionType.MODIFIED}
                    sx={unselectedProgramLabelStyles}
                    textSelector="span"
                  >
                    <span>Program Unselected</span>
                  </PendingChangeWrapper>
                </CellContent>
              )}
            </Stack>
          );
        }

        case ColumnIds.PROGRAM_NAME: {
          return (
            <Stack sx={programCellContentStyles}>
              <CellContent sourceValue={programInternalName}>
                {_.toUpper(programInternalName)}
                {isLoanOptimalBlueOrSifterPPEType && (
                  <ButtonWithTooltip
                    data-testid={ProgramsAndPricingTestIds.SEARCH_ID_BUTTON}
                    icon={InfoIcon}
                    iconButtonClassName="p-0 mb-10"
                    iconSize={10}
                    placement="bottom"
                    spanContainerClassName="ml-auto"
                    title={`Search ID: ${searchId}`}
                    withCopiedValue={searchId}
                  />
                )}
              </CellContent>
              {!!lastUpdatedDateStr && (
                <CellContent sourceValue={lastUpdatedDateStr} type={CellContentType.SECONDARY}>
                  Last Updated: {lastUpdatedDateStr}
                </CellContent>
              )}
            </Stack>
          );
        }

        case ColumnIds.RATE: {
          if (program.ineligibleStatus) {
            return null;
          }

          return (
            <PendingChangeWrapper
              isSmall
              pendingChangesActionType={
                !isPendingProgramChange &&
                isSelectedLoanProgramOption &&
                !!(pendingChanges.apr || pendingChanges.interestRate)
                  ? PendingChangesActionType.MODIFIED
                  : undefined
              }
              textSelector="p,span"
            >
              <Stack sx={amountCellContentStyles}>
                <CellContent sourceValue={rate}>{`${formatNumberToFixed3Decimals(
                  rate,
                )}%`}</CellContent>
                <CellContent sourceValue={apr} type={CellContentType.SECONDARY}>
                  {_.isNumber(apr) ? `${formatNumberToFixed3Decimals(apr)}% APR` : apr}
                </CellContent>
              </Stack>
            </PendingChangeWrapper>
          );
        }

        case ColumnIds.CREDIT: {
          if (program.ineligibleStatus) {
            return null;
          }

          return (
            <>
              {!!(iconButtonProps && getTooltip && !isStubLoanOption) && (
                <Box sx={tooltipWrapperStyles}>
                  <ButtonWithTooltip
                    data-testid={ProgramsAndPricingTestIds.ADJUSTMENTS_INFO_BUTTON}
                    title={getTooltip?.({ ...currentLoanProgramOption, customAdjustments })}
                    {...iconButtonProps}
                  />
                </Box>
              )}

              <Stack sx={amountCellContentStyles}>
                <CellContent sourceValue={creditOrCost}>
                  {formatNumberToCurrencyString(creditOrCost, creditOrCostCurrencyOptions)}
                </CellContent>
                <CellContent sourceValue={compensation} type={CellContentType.SECONDARY}>
                  {formatNumberToCurrencyString(
                    compensation,
                    fixedTwoFractionDigitsCurrencyOptions,
                  )}
                </CellContent>
              </Stack>
            </>
          );
        }

        case ColumnIds.PAYMENT: {
          if (program.ineligibleStatus) {
            return null;
          }

          return (
            <PendingChangeWrapper
              isSmall
              pendingChangesActionType={
                !isPendingProgramChange &&
                isSelectedLoanProgramOption &&
                !!pendingChanges.principalAndInterest
                  ? PendingChangesActionType.MODIFIED
                  : undefined
              }
              textSelector="p,span"
            >
              <CellContent sourceValue={paymentMonth}>
                {_.isNumber(paymentMonth)
                  ? formatNumberToCurrencyString(
                      paymentMonth,
                      fixedTwoFractionDigitsCurrencyOptions,
                    )
                  : paymentMonth}
              </CellContent>
              <CellContent sourceValue={pmi > 0} type={CellContentType.SECONDARY}>
                {`(${formatNumberToCurrencyString(
                  pmi + paymentMonth,
                  fixedTwoFractionDigitsCurrencyOptions,
                )})`}
              </CellContent>
            </PendingChangeWrapper>
          );
        }

        case ColumnIds.LOCK_PERIOD: {
          return (
            <PendingChangeWrapper
              isSmall
              pendingChangesActionType={
                !isPendingProgramChange && isSelectedLoanProgramOption && !!pendingChanges.lockTerm
                  ? PendingChangesActionType.MODIFIED
                  : undefined
              }
              textSelector="p,span"
            >
              <CellContent sourceValue={lockTerm}>{`${lockTerm} Days`}</CellContent>
            </PendingChangeWrapper>
          );
        }

        default:
          return null;
      }
    },
    [
      vendorType,
      lenderName,
      isPendingInitProgram,
      isPendingNewProgram,
      isPendingProgramChange,
      programInternalName,
      lastUpdatedDateStr,
      isSelectedLoanProgramOption,
      pendingChanges.apr,
      pendingChanges.interestRate,
      pendingChanges.principalAndInterest,
      pendingChanges.lockTerm,
      rate,
      apr,
      isStubLoanOption,
      currentLoanProgramOption,
      customAdjustments,
      creditOrCost,
      compensation,
      paymentMonth,
      pmi,
      lockTerm,
      isLoanOptimalBlueOrSifterPPEType,
      searchId,
      program.ineligibleStatus,
    ],
  );

  const eligibilityTooltip = useMemo(
    () =>
      getEligibilityTooltip({
        isEligible,
        isProgramExpired,
        denialReasons: eligibility?.denialReasons,
        expiredMessage: ExpiredPriceWarningMessage,
      }),
    [isEligible, isProgramExpired, eligibility],
  );

  const status = useMemo(() => {
    if (!isEligible) {
      return StatusEnum.ERROR;
    }

    if (isProgramExpired) {
      return StatusEnum.WARNING;
    }

    if (_.get(programDetails, ['rateCalculations', 'prices', 'length'])) {
      return StatusEnum.SUCCESS;
    }

    return StatusEnum.WARNING;
  }, [isEligible, isProgramExpired, programDetails]);

  return (
    <Stack sx={programSummaryStyles}>
      <Box align={TABLE_CELL_ALIGNMENT.CENTER} key={ColumnIds.ELIGIBILITY}>
        <Tooltip
          PopperProps={tooltipPopperProps}
          placement="bottom"
          title={
            eligibilityTooltip ? <Box sx={eligibilityTooltipStyles}>{eligibilityTooltip}</Box> : ''
          }
        >
          <Box component="span">
            <StatusIcon status={status} />
          </Box>
        </Tooltip>
      </Box>
      {columnsDefs.map(({ id: columnId, style, align, isSort, getTooltip, iconButtonProps }) => (
        <Box
          align={!isSort ? align : TABLE_CELL_ALIGNMENT.INHERIT}
          key={columnId}
          sx={getCellStyles({
            isPendingNewProgram,
            isPendingInitProgram,
            customStyles: style,
            isRightIndent: (isSort || _.isNil(isSort)) && align === HORIZONTAL_ALIGNMENT.RIGHT,
          })}
        >
          {cellContentRenderer({ columnId, getTooltip, iconButtonProps })}
        </Box>
      ))}
    </Stack>
  );
};

ProgramSummary.propTypes = {
  columnsDefs: PropTypes.arrayOf(ColumnDefShape).isRequired,
  programDetails: PropTypes.object.isRequired,
  currentLoanProgramOption: PropTypes.object.isRequired,
  pendingChanges: PropTypes.object,
  isPendingInitProgram: PropTypes.bool,
  isPendingNewProgram: PropTypes.bool,
  isPendingProgramChange: PropTypes.bool,
  isProgramExpired: PropTypes.bool,
  isSelectedLoanProgramOption: PropTypes.bool,
};

export default memo(ProgramSummary);
